/**
 * V38.10 — Panneau de gestion Organisations (multi-tenant CCMI).
 *
 * Permet au Super Admin de gérer les organisations (cabinet juriste, structure ESS,
 * courtier, constructeur) et de lier/délier des utilisateurs.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Building2, Pencil, Trash2, Plus, Users, FolderOpen, Loader2, UserPlus, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ORG_TYPES = [
  { value: 'cabinet_juriste', label: 'Cabinet juriste' },
  { value: 'structure_ess', label: 'Structure ESS' },
  { value: 'courtier', label: 'Courtier' },
  { value: 'constructeur', label: 'Constructeur' },
  { value: 'autre', label: 'Autre' },
];

const EMPTY_ORG = {
  name: '',
  slug: '',
  type: 'structure_ess',
  siret: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  territoire: '',
  active: true,
  notes: '',
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function OrganizationsManagementPanel() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Membres dialog
  const [membersDialog, setMembersDialog] = useState(null); // { org, members }
  const [memberInput, setMemberInput] = useState('');
  const [memberRole, setMemberRole] = useState('member');

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/organizations`, { headers: getAuthHeader() });
      setOrgs(res.data?.organizations || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const openCreate = () => setEditing({ ...EMPTY_ORG, _mode: 'create' });
  const openEdit = (org) => setEditing({ ...org, _mode: 'edit' });

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload._mode;
      delete payload.id;
      delete payload.member_count;
      delete payload.ccmi_projects_count;
      delete payload.members;
      // Nettoyage : null pour champs optionnels vides (cohérence backend)
      ['siret', 'contact_email', 'contact_phone', 'address', 'territoire', 'notes'].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      if (editing._mode === 'create') {
        await axios.post(`${API}/admin/organizations`, payload, { headers: getAuthHeader() });
        toast.success('Organisation créée');
      } else {
        delete payload.slug; // immuable
        await axios.put(`${API}/admin/organizations/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success('Organisation mise à jour');
      }
      setEditing(null);
      await fetchOrgs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Supprimer l'organisation « ${org.name} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/organizations/${org.id}`, { headers: getAuthHeader() });
      toast.success('Organisation supprimée');
      await fetchOrgs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const openMembers = async (org) => {
    try {
      const res = await axios.get(`${API}/admin/organizations/${org.id}`, { headers: getAuthHeader() });
      setMembersDialog({ org: res.data?.organization, members: res.data?.organization?.members || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement membres');
    }
  };

  const handleAddMember = async () => {
    const v = (memberInput || '').trim();
    if (!v || !membersDialog) return;
    try {
      await axios.post(
        `${API}/admin/organizations/${membersDialog.org.id}/members`,
        { user_id: v, role_in_org: memberRole },
        { headers: getAuthHeader() },
      );
      toast.success('Membre rattaché');
      setMemberInput('');
      await openMembers(membersDialog.org);
      await fetchOrgs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur ajout');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!membersDialog) return;
    try {
      await axios.delete(
        `${API}/admin/organizations/${membersDialog.org.id}/members/${userId}`,
        { headers: getAuthHeader() },
      );
      toast.success('Membre détaché');
      await openMembers(membersDialog.org);
      await fetchOrgs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur retrait');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="organizations-loading">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <Card data-testid="organizations-panel">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-indigo-900">
              <Building2 className="h-5 w-5" />
              Organisations — Multi-tenant CCMI
            </CardTitle>
            <CardDescription>
              Gestion des tenants CCMI (cabinet juriste / structure ESS / courtier / constructeur).
              Chaque utilisateur rattaché à une organisation ne voit que les dossiers de son organisation.
            </CardDescription>
          </div>
          <Button onClick={openCreate} className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="organizations-create-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle organisation
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-3">
        {orgs.length === 0 ? (
          <p className="italic text-slate-500">Aucune organisation définie. Créez-en une pour commencer.</p>
        ) : orgs.map((org) => (
          <div
            key={org.id}
            className={`border rounded-lg p-4 flex flex-wrap items-start gap-3 ${org.active ? 'bg-white' : 'bg-slate-50 opacity-70'}`}
            data-testid={`organization-row-${org.slug}`}
          >
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-slate-900">{org.name}</h4>
                <Badge variant="outline" className="font-mono text-[10px]">{org.slug}</Badge>
                <Badge variant="outline" className="text-[10px]">{org.type}</Badge>
                {org.territoire && <Badge variant="outline" className="text-[10px] text-blue-700">{org.territoire}</Badge>}
                {!org.active && <Badge variant="outline" className="text-[10px] text-slate-500">Inactive</Badge>}
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {org.contact_email && <>📧 {org.contact_email} • </>}
                {org.contact_phone && <>📞 {org.contact_phone} • </>}
                {org.siret && <>SIRET : {org.siret}</>}
              </p>
              {org.notes && <p className="text-xs text-slate-500 mt-1 italic">{org.notes}</p>}
              <div className="text-xs text-slate-700 mt-2 flex items-center gap-3">
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {org.member_count || 0} membres</span>
                <span className="flex items-center gap-1"><FolderOpen className="h-3 w-3" /> {org.ccmi_projects_count || 0} dossiers CCMI</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => openMembers(org)} data-testid={`organization-members-${org.slug}`}>
                <UserPlus className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(org)} data-testid={`organization-edit-${org.slug}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(org)}
                      className="text-red-700 hover:bg-red-50"
                      data-testid={`organization-delete-${org.slug}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>

      {/* Dialog création / édition */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="organization-dialog">
          <DialogHeader>
            <DialogTitle>{editing?._mode === 'create' ? 'Nouvelle organisation' : `Modifier : ${editing?.name || ''}`}</DialogTitle>
            <DialogDescription>
              Les utilisateurs rattachés ne verront que les dossiers CCMI de leur organisation.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={editing.name || ''}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    data-testid="organization-field-name"
                  />
                </div>
                <div>
                  <Label>Slug (immuable)</Label>
                  <Input
                    value={editing.slug || ''}
                    disabled={editing._mode === 'edit'}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="auto si vide"
                    data-testid="organization-field-slug"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <select
                    value={editing.type || 'structure_ess'}
                    onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                    className="w-full border rounded px-2 py-2 text-sm"
                    data-testid="organization-field-type"
                  >
                    {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Territoire</Label>
                  <Input
                    placeholder="GP, MQ, GF, RE, YT…"
                    value={editing.territoire || ''}
                    onChange={(e) => setEditing({ ...editing, territoire: e.target.value })}
                    data-testid="organization-field-territoire"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>SIRET</Label>
                  <Input value={editing.siret || ''} onChange={(e) => setEditing({ ...editing, siret: e.target.value })} data-testid="organization-field-siret" />
                </div>
                <div>
                  <Label>Email contact</Label>
                  <Input value={editing.contact_email || ''} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} data-testid="organization-field-email" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={editing.contact_phone || ''} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} data-testid="organization-field-phone" />
                </div>
              </div>
              <div>
                <Label>Adresse</Label>
                <Input value={editing.address || ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} data-testid="organization-field-address" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={editing.notes || ''}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  data-testid="organization-field-notes"
                />
              </div>
              <div className="flex items-center justify-between border rounded px-3 py-2">
                <Label htmlFor="org-active">Active</Label>
                <Switch
                  id="org-active"
                  checked={!!editing.active}
                  onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                  data-testid="organization-field-active"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="organization-dialog-cancel">Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-700 hover:bg-indigo-800" data-testid="organization-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog membres */}
      <Dialog open={!!membersDialog} onOpenChange={(o) => { if (!o) setMembersDialog(null); }}>
        <DialogContent className="max-w-xl" data-testid="organization-members-dialog">
          <DialogHeader>
            <DialogTitle>Membres : {membersDialog?.org?.name}</DialogTitle>
            <DialogDescription>
              Liez des utilisateurs (email ou ID) à cette organisation.
            </DialogDescription>
          </DialogHeader>
          {membersDialog && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="email ou user_id"
                  value={memberInput}
                  onChange={(e) => setMemberInput(e.target.value)}
                  data-testid="organization-member-input"
                />
                <select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value)}
                  className="border rounded px-2 text-sm"
                  data-testid="organization-member-role"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="juriste">Juriste</option>
                  <option value="owner">Owner</option>
                </select>
                <Button onClick={handleAddMember} className="bg-indigo-700 hover:bg-indigo-800" data-testid="organization-member-add">
                  Ajouter
                </Button>
              </div>
              {membersDialog.members.length === 0 ? (
                <p className="italic text-slate-500">Aucun membre.</p>
              ) : (
                <div className="border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs">
                      <tr>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Rôle</th>
                        <th className="px-3 py-2">Rôle org</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {membersDialog.members.map((m) => (
                        <tr key={m.id} className="border-t" data-testid={`organization-member-row-${m.email}`}>
                          <td className="px-3 py-2 font-mono text-[11px]">{m.email}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{m.role || '—'}</Badge></td>
                          <td className="px-3 py-2 text-xs">{m.role_in_org || '—'}</td>
                          <td className="px-3 py-2">
                            <Button size="sm" variant="ghost" onClick={() => handleRemoveMember(m.id)} className="text-red-700">
                              <X className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMembersDialog(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
